import React from 'react';
import { GameState } from '../../domain/entities';
import { soundService } from '../../services/SoundService';
import './TransferSuggestionsPanel.css';

interface TransferSuggestionsPanelProps {
    gameState: GameState;
    onAcceptSuggestion: (suggestionId: string) => void;
    onRejectSuggestion: (suggestionId: string) => void;
}

export const TransferSuggestionsPanel: React.FC<TransferSuggestionsPanelProps> = ({
    gameState,
    onAcceptSuggestion,
    onRejectSuggestion
}) => {
    const playerTeam = Object.values(gameState.leagues)
        .flatMap(l => l.teams)
        .find(t => t.name === gameState.playerTeamName);

    if (!playerTeam) return null;

    const suggestions = playerTeam.assistantManager.transferSuggestions || [];
    const pendingSuggestions = suggestions.filter(s => s.status === 'pending');

    if (pendingSuggestions.length === 0) {
        return (
            <div className="transfer-suggestions-panel">
                <div className="panel-header">Assistant Manager Transfer Recommendations</div>
                <div style={{ padding: '12px', opacity: 0.7 }}>No pending transfer recommendations</div>
            </div>
        );
    }

    const handleAccept = (suggestionId: string) => {
        soundService.playClick();
        onAcceptSuggestion(suggestionId);
    };

    const handleReject = (suggestionId: string) => {
        soundService.playClick();
        onRejectSuggestion(suggestionId);
    };

    return (
        <div className="transfer-suggestions-panel">
            <div className="panel-header">
                Assistant Manager Transfer Recommendations ({pendingSuggestions.length})
            </div>

            <div className="suggestions-list">
                {pendingSuggestions.map(suggestion => (
                    <div key={suggestion.id} className="suggestion-item">
                        <div className="suggestion-content">
                            <div className="suggestion-title">
                                <span className="action-badge" data-action={suggestion.action}>
                                    {suggestion.action.toUpperCase()}
                                </span>
                                <span className="player-name">{suggestion.playerName}</span>
                                {suggestion.position && (
                                    <span className="player-position">{suggestion.position}</span>
                                )}
                            </div>
                            <div className="suggestion-stats">
                                {suggestion.skill !== undefined && (
                                    <span>Skill: <strong>{suggestion.skill}</strong></span>
                                )}
                                {suggestion.age !== undefined && (
                                    <span>Age: <strong>{suggestion.age}</strong></span>
                                )}
                                {suggestion.suggestedPrice !== undefined && (
                                    <span>Price: <strong>£{suggestion.suggestedPrice.toLocaleString()}</strong></span>
                                )}
                            </div>
                            <div className="suggestion-reason">
                                {suggestion.reason}
                            </div>
                        </div>

                        <div className="suggestion-actions">
                            <button
                                className="btn-accept"
                                onClick={() => handleAccept(suggestion.id)}
                                title="Accept this recommendation"
                            >
                                ✓ Accept
                            </button>
                            <button
                                className="btn-reject"
                                onClick={() => handleReject(suggestion.id)}
                                title="Reject this recommendation"
                            >
                                ✕ Reject
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {suggestions.some(s => s.status !== 'pending') && (
                <div className="suggestions-history">
                    <div className="history-header">Recent Actions</div>
                    {suggestions
                        .filter(s => s.status !== 'pending')
                        .slice(-3)
                        .map(suggestion => (
                            <div key={suggestion.id} className="history-item">
                                <span className="status-badge" data-status={suggestion.status}>
                                    {suggestion.status.toUpperCase()}
                                </span>
                                <span>{suggestion.playerName} ({suggestion.action})</span>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};
